"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { 
  Code, 
  LayoutDashboard, 
  Send, 
  Settings, 
  Users, 
  Wallet, 
  Landmark, 
  FileText, 
  ArrowRightLeft,
  ChevronsUpDown, 
  PanelLeftClose,
  PanelLeftOpen,
  LifeBuoy,
  Zap,
  UserCog
} from "lucide-react";
import { cn } from "@/lib/utils";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";

const navGroups = [
  {
    label: "Overview",
    items: [
      { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
    ]
  },
  {
    label: "Operations",
    items: [
      { name: "Transactions", href: "/dashboard/transactions", icon: ArrowRightLeft },
      { name: "Payouts", href: "/dashboard/payouts", icon: Send },
      { name: "Customers", href: "/dashboard/customers", icon: Users },
    ]
  },
  {
    label: "Financials",
    items: [
      { name: "Balances", href: "/dashboard/balances", icon: Wallet },
      { name: "Invoicing", href: "/dashboard/invoicing", icon: FileText },
      { name: "Virtual Accounts", href: "/dashboard/virtual-accounts", icon: Landmark },
    ]
  },
  {
    label: "System & Support",
    items: [
      { name: "Users & Team", href: "/dashboard/team", icon: UserCog },
      { name: "Developers", href: "/dashboard/api", icon: Code },
      { name: "Settings", href: "/dashboard/settings", icon: Settings },
      { name: "Support Hub", href: "/dashboard/support", icon: LifeBuoy },
    ]
  }
];

export function Sidebar({ onMobileClose }: { onMobileClose?: () => void }) {
  const pathname = usePathname();
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <div className={cn(
      "flex h-full flex-col border-r border-border bg-card text-card-foreground py-4 transition-all duration-300 relative", 
      isCollapsed ? "w-16 px-2" : "w-64 px-4"
    )}>
      
      {/* Brand Header */}
      <div className={cn("mb-6 px-1 flex items-center justify-between h-12 border-b border-border/60 pb-3", isCollapsed && "flex-col justify-center gap-2 px-0")}>
        {!isCollapsed ? (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex-1 focus:outline-none">
                <div className="flex items-center justify-between gap-2.5 p-1.5 rounded-lg hover:bg-muted/60 transition-colors cursor-pointer border border-transparent hover:border-border">
                  <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center shrink-0 shadow-sm">
                      <Zap className="h-4 w-4 text-primary-foreground fill-current" />
                    </div>
                    <div className="flex flex-col text-left overflow-hidden">
                      <span className="text-sm font-bold tracking-tight text-foreground truncate">PayTaara</span>
                      <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Live Production</span>
                    </div>
                  </div>
                  <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="start">
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-[10px] uppercase font-semibold text-muted-foreground px-2 py-1.5">Switch Environment</DropdownMenuLabel>
                  <DropdownMenuItem className="cursor-pointer font-medium text-xs">
                    <div className="flex items-center gap-2">
                      <div className="h-5 w-5 rounded bg-primary/20 flex items-center justify-center text-primary font-bold text-[10px]">P</div>
                      <span className="text-xs font-semibold">PayTaara Production</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer opacity-70 font-medium text-xs">
                    <div className="flex items-center gap-2">
                      <div className="h-5 w-5 rounded bg-muted flex items-center justify-center text-muted-foreground font-bold text-[10px]">S</div>
                      <span className="text-xs font-semibold">Sandbox Test Environment</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <button 
              onClick={() => setIsCollapsed(true)}
              title="Collapse Sidebar"
              className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center shadow-sm">
              <Zap className="h-4 w-4 text-primary-foreground fill-current" />
            </div>
            <button 
              onClick={() => setIsCollapsed(false)}
              title="Expand Sidebar"
              className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              <PanelLeftOpen className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Navigation Groups */}
      <div className="flex-1 space-y-5 overflow-y-auto no-scrollbar">
        {navGroups.map((group) => (
          <div key={group.label} className="space-y-1">
            {!isCollapsed && (
              <h3 className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-2">
                {group.label}
              </h3>
            )}
            <nav className="space-y-1">
              {group.items.map((item) => {
                const isActive = pathname === item.href || (item.href !== "/dashboard" && pathname.startsWith(item.href));
                return (
                  <Link
                    key={item.name}
                    href={item.href}
                    onClick={() => onMobileClose?.()}
                    title={isCollapsed ? item.name : undefined}
                    className={cn(
                      "group flex items-center rounded-lg py-2 text-sm font-medium transition-colors",
                      isCollapsed ? "justify-center px-0" : "px-3",
                      isActive
                        ? "bg-primary/10 text-primary font-semibold"
                        : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                    )}
                  >
                    <item.icon
                      className={cn(
                        "h-4 w-4 shrink-0 transition-transform group-hover:scale-105",
                        !isCollapsed && "mr-3",
                        isActive ? "text-primary" : "text-muted-foreground/70 group-hover:text-foreground"
                      )}
                    />
                    {!isCollapsed && <span className="truncate">{item.name}</span>}
                  </Link>
                );
              })}
            </nav>
          </div>
        ))}
      </div>

      {/* Footer / User Profile */}
      <div className="mt-auto pt-4 border-t border-border space-y-3">
        {!isCollapsed && (
          <div className="px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-2.5">
            <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
            <div className="flex flex-col overflow-hidden">
              <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">System Operational</span>
              <span className="text-[9px] text-muted-foreground font-medium">Gateway Latency: 42ms</span>
            </div>
          </div>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger className="w-full focus:outline-none">
            <div className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted/60 transition-colors cursor-pointer w-full text-left border border-transparent hover:border-border",
              isCollapsed && "justify-center px-0"
            )}>
              <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0 border border-primary/20">
                JD
              </div>
              {!isCollapsed && (
                <div className="flex flex-col overflow-hidden">
                  <span className="text-sm font-semibold text-foreground truncate">John Doe</span>
                  <span className="text-[10px] text-muted-foreground font-medium">Administrator</span>
                </div>
              )}
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-52" align={isCollapsed ? "center" : "start"} side={isCollapsed ? "right" : "top"} sideOffset={10}>
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground">My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link href="/dashboard/profile" className="w-full cursor-pointer text-xs font-medium py-2" onClick={() => onMobileClose?.()}>Profile & Account</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/dashboard/settings" className="w-full cursor-pointer text-xs font-medium py-2" onClick={() => onMobileClose?.()}>Settings & Security</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/dashboard/upgrade" className="w-full cursor-pointer text-xs font-medium py-2 text-primary" onClick={() => onMobileClose?.()}>Upgrade Tier</Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link href="/login" className="w-full cursor-pointer text-xs font-medium py-2 text-destructive focus:text-destructive focus:bg-destructive/10">
                Sign Out
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

